
import { useGameMode, GameMode } from '@/contexts/GameModeContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useState, useMemo } from 'react';
import { clearCachedOverlayState } from './game/managers/OverlayManager';

export const GameModeSelector = () => {
  // Temporarily disable to fix infinite loop
  return null;
  
  const { currentMode, availableModes, switchToMode } = useGameMode();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  // Memoize enabled modes calculation to prevent infinite re-renders
  const enabledModes = useMemo(() => {
    return Object.entries(availableModes)
      .filter(([_, config]) => config.isEnabled)
      .length;
  }, [availableModes]);
    
  if (enabledModes < 2) return null;

  const handleSelectMode = async (mode: GameMode) => {
    // Only allow selecting enabled modes
    if (availableModes[mode].isEnabled && mode !== currentMode) {
      try {
        console.log(`User selected mode: ${mode} from dropdown`, {
          timestamp: new Date().toISOString(),
          previousMode: currentMode,
          newMode: mode
        });
        
        // Close dropdown to avoid any state issues while switching
        setIsMenuOpen(false);
        
        // Set switching state to prevent multiple clicks
        setIsSwitching(true);
        
        
        // Clear the overlay cache before switching mode
        clearCachedOverlayState();
        
        // Use the switchToMode function
        const success = await switchToMode(mode, true);
        
        if (!success) {
          toast.error('Failed to switch game mode. Please try again.');
        }
      } catch (error) {
        console.error('Error switching game mode:', error);
        toast.error('Failed to switch game mode. Please try again.');
      } finally {
        // Reset switching state
        setIsSwitching(false);
      }
    }
  };

  return (
    <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="flex items-center gap-2 bg-gray-800 border-gray-700 text-gray-200"
          disabled={isSwitching}
        >
          {isSwitching ? 'Switching...' : availableModes[currentMode].name}
          <ChevronDown size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-gray-800 border-gray-700 text-gray-200">
        {Object.entries(availableModes).map(([mode, config]) => (
          <DropdownMenuItem
            key={mode}
            onClick={() => handleSelectMode(mode as GameMode)}
            disabled={!config.isEnabled || isSwitching || mode === currentMode}
            className={`${mode === currentMode ? 'bg-gray-700' : ''} ${!config.isEnabled || isSwitching ? 'text-gray-500 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-700'}`}
          >
            <div className="flex flex-col">
              <span className="font-medium">{config.name}</span>
              <span className="text-xs text-gray-400">{config.description}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
